import React, { useEffect, useRef, useState } from 'react'
import { FormContainer } from 'react-hook-form-mui'
import { submitStudentSurvey, submitStudentSurveyPublic } from '../../store/slices/studentSurveyListSlice/apis'
import { useNavigate, useParams } from 'react-router-dom'
import ROUTES from '../../consts/paths'
import CustomHeader from '../../components/CustomHeader'
import { useAppDispatch } from '../../store'
import { getStudentSelectedSurvey } from '../../store/slices/studentSurveyListSlice/selectors'
import { notification } from '../../components/Notifications'
import PageHeader from '../../components/PageHeader'
import { type OnSubmitFn } from './components/SurveyFeatureStepper/types'
import { StepperContextProvider } from './components/SurveyFeatureStepper/useStepper'
import { RenderSections } from './components/RenderSections'
import UnsavedBlockerModal from '../../components/UnsavedBlockerModal'
import { UserSelectInstructorsCard } from './components/UserSelectInstructorsCard'
interface SurveyFeatureProps {
  isPublic?: boolean
}
interface SurveyType {
  surveySections?: any[]
  [key: string]: any
}

const SurveyFeature: React.FC<SurveyFeatureProps> = ({ isPublic = false }): JSX.Element => {
  const dispatch = useAppDispatch()
  const { id } = useParams()
  const [survey, setSurvey] = useState<any | null>(null)
  const initialFetchedSurveys = getStudentSelectedSurvey() as SurveyType | null
  const [isUserSelectEnabled, setIsUserSelectEnabled] = useState<boolean>(() =>
    initialFetchedSurveys?.surveySections?.some(
      section => section?.isSelectInstructorEnabled === true && section?.isRepeatPerInstructor === true
    ) ?? false
  )
  useEffect(() => {
    if (initialFetchedSurveys) {
      setSurvey(initialFetchedSurveys)
    }
  }, [])
  const navigate = useNavigate()
  const [surveyResponseMap, setSurveyResponseMap] = useState<Record<string, string | string[]>>({})
  const isLoadedFromLocalStorage = useRef(false)

  const storeKey = `surveyDraft-${id}`

  useEffect(() => {
    if (isLoadedFromLocalStorage.current) return
    isLoadedFromLocalStorage.current = true
    const parsed = JSON.parse(localStorage.getItem(storeKey) ?? '{}')
    if (Object.keys(parsed)?.length !== 0) notification('Reloading previous responses', { variant: 'info' })
    setSurveyResponseMap(parsed)
  }, [storeKey])

  const onSubmit: OnSubmitFn = async (responseItems): Promise<void> => {
    const payload = {
      status: 'Submitted',
      surveyResponseItems: responseItems
    }
    try {
      if (survey) {
        let res
        if (isPublic) {
          res = await dispatch(submitStudentSurveyPublic({ urlId: id, data: payload })).unwrap()
          if (res) {
            localStorage.removeItem(storeKey)
            notification('Survey added successfully', { variant: 'success' })
            setTimeout(() => { navigate(ROUTES.SURVEY_COMPLETED_PUBLIC, { replace: true }) }, 0)
          }
        } else {
          res = await dispatch(submitStudentSurvey({ urlId: id, data: payload })).unwrap()
          if (res) {
            localStorage.removeItem(storeKey)
            setTimeout(() => { navigate(ROUTES.SURVEY_COMPLETED) }, 0)
          }
        }
      }
    } catch (error: any) {
      notification(error?.message ?? 'An error occoured while submitting this survey', { variant: 'error' })
    }
  }

  const instructorTitles: string = survey && Array.isArray(survey.instructors)
    ? survey.instructors.map((instructor: { title: string }) => instructor.title).join(', ')
    : ''
  const sectionAbbreviation: string = typeof survey?.sectionAbbreviation === 'string' ? survey.sectionAbbreviation : ''
  const description = survey ? `${sectionAbbreviation}${instructorTitles ? ', ' + instructorTitles : ''}` : ''

  return (
    <React.Fragment>
      <CustomHeader info={survey?.sectionAbbreviation} />
      <FormContainer
        key={JSON.stringify(Object.keys(surveyResponseMap))}
        defaultValues={surveyResponseMap}
      >
        <StepperContextProvider>
          <PageHeader
            title={survey ? survey.distributionSeriesTitle : ''}
            description={description}
            isCompactOnly
            showBackButton={!isPublic}
          />
      {!isUserSelectEnabled && <RenderSections rawSections={survey?.surveySections ?? []} onSubmit={onSubmit} />}
        </StepperContextProvider>
      </FormContainer>
      {isUserSelectEnabled && <UserSelectInstructorsCard survey={survey} setSurvey={setSurvey} setIsUserSelectEnabled={setIsUserSelectEnabled} />}
      <UnsavedBlockerModal storeKey={storeKey} title='Leaving Survey' confirmActionText='Leave'>
        Your answers will not be saved. Are you sure you want to leave?
      </UnsavedBlockerModal>
    </React.Fragment>
  )
}

export { SurveyFeature }
export default SurveyFeature
